import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ENVIRONMENT_SERVICE_TOKEN, ProductCategoryEnum } from '@mwe/constants';
import {
  Address,
  HTTPError,
  IDefaultProdukt,
  INewClientConfirmationStatusResponse,
  IOlavEntry,
  IOrderDetailsInfo,
  IPersonalData,
  IProduktVerfuegbarkeit,
  ISolrOlavKeyAddress,
  NewClientDetailsDTO,
} from '@mwe/models';
import { catchError, Observable, throwError } from 'rxjs';
import staticConfig from 'libs/services/src/lib/config/products.config.json';
import { EnvironmentService, NewClientStore, OrderService } from '@mwe/services';

@Injectable({
  providedIn: 'root',
})
export class OrderNewClientService {
  private readonly http = inject(HttpClient);
  private readonly environmentService = inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN);
  private readonly orderService = inject(OrderService);
  private readonly newClientStore = inject(NewClientStore);

  private readonly config: any;

  constructor() {
    this.config = staticConfig;
  }

  getConfigPossibleCategories(): any {
    return this.config.possibleCategories;
  }

  getMultiProductCardCategories(): any {
    return this.config.multiProductCardCategories;
  }

  getAvailability(selectedOlav: IOlavEntry): Promise<IProduktVerfuegbarkeit[]> {
    return this.orderService.getAvailability(selectedOlav.identAdresse ? selectedOlav.identAdresse : selectedOlav);
  }

  getMultiProductTariffRestrictions(): any {
    return this.config.multiProductTariffRestrictions;
  }

  getAlreadySelectedProductCategories(orderDetails: IOrderDetailsInfo[]): ProductCategoryEnum[] {
    return orderDetails.filter(elem => elem.status === 'Anmeldung').map(elem => ProductCategoryEnum[elem.category.toUpperCase()]);
  }

  getIpAddress(): Observable<{ remoteAddress: string }> {
    return this.http.get<{ remoteAddress: string }>('/api/public/newclient/ip-address');
  }

  getConfigDefaultTariffs(): IDefaultProdukt {
    const customerType = this.environmentService.getPortalCustomerType();
    return {
      strom: {
        tarifBeschreibung: this.config.tariffs.strom[customerType].name,
        tarifKey: this.config.tariffs.strom[customerType].shortcut,
      },
      gas: {
        tarifBeschreibung: this.config.tariffs.gas[customerType].name,
        tarifKey: this.config.tariffs.gas[customerType].shortcut,
      },
    };
  }

  confirm$(addressCode: string, newClientType: number, dataDTO: NewClientDetailsDTO): Observable<INewClientConfirmationStatusResponse> {
    return this.http
      .post<INewClientConfirmationStatusResponse>(
        this.orderService.getResourceUrl('confirm') + '?addressCode=' + addressCode + '&newClientType=' + newClientType,
        dataDTO,
      )
      .pipe(catchError(err => throwError(() => new HTTPError(err.status, err.error, err.error?.message))));
  }

  retry$(submitId: number, newClientType: number, dataDTO: NewClientDetailsDTO): Observable<INewClientConfirmationStatusResponse> {
    return this.http
      .put<INewClientConfirmationStatusResponse>(
        this.orderService.getResourceUrl('retry') + '?submitId=' + submitId + '&newClientType=' + newClientType,
        dataDTO,
      )
      .pipe(catchError(err => throwError(() => new HTTPError(err.status, err.error))));
  }

  confirmStatus$(submitId: number, newClientType: number): Observable<INewClientConfirmationStatusResponse> {
    const url = this.orderService.getResourceUrl('confirmStatus') + '?submitId=' + submitId + '&newClientType=' + newClientType;
    return this.http.get<INewClientConfirmationStatusResponse>(url);
  }

  //TODO: When old Address Input gets deleted refactor to Observable.
  async getAddressFromOlavKey(olavKey: string, processType?: string): Promise<ISolrOlavKeyAddress> {
    return this.orderService.getAddressFromOlavKey(olavKey, processType);
  }

  sendProductInterestRequest$(personalData: IPersonalData, address: Address): Observable<string> {
    return this.http.post<string>(this.orderService.getResourceUrl('productInterest'), {
      personalData,
      address,
    });
  }
}
